import styled, { css } from 'styled-components';

type CSSProps = React.CSSProperties;

type GridContainerProps =
  | {
      container: true;
      gridSize?: number;
      rowGap?: number;
      columnGap?: number;
    }
  | { container?: false };

type GridItemProps =
  | {
      item: true;
      size?: number;
      nextRow?: boolean;
      startColumn?: number;
    }
  | { item?: false };

const StyledGrid = styled.div<GridContainerProps & GridItemProps>`
  ${(props) => {
    if (!props.container) return css``;
    const { gridSize = 12, rowGap = 0, columnGap = 0 } = props;
    return css`
      display: grid;
      grid-template-columns: repeat(${gridSize}, 1fr);
      row-gap: ${rowGap}px;
      column-gap: ${columnGap}px;
    `;
  }}

  ${(props) => {
    if (!props.item) return css``;
    const { size = 1, nextRow, startColumn } = props;
    if (!nextRow && !startColumn) {
      return css`
        grid-column: span ${size};
      `;
    }

    const columnLine = nextRow ? 1 : startColumn ?? 1;

    return css`
      grid-column: ${columnLine} / span ${size};
    `;
  }}
`;

const Container = styled.div<{
  gridSize?: number;
  rowGap?: number;
  columnGap?: number;
}>`
  ${({ gridSize = 12, rowGap = 0, columnGap = 0 }) => css`
    display: grid;
    grid-template-columns: repeat(${gridSize}, 1fr);
    row-gap: ${rowGap}px;
    column-gap: ${columnGap}px;
  `}
`;

const Item = styled.div<
  {
    size?: number;
    nextRow?: boolean;
    startColumn?: number;
    alignCenter?: boolean;
    justifyCenter?: boolean;
    alignItems?: CSSProps['alignItems'];
    justifyContent?: CSSProps['justifyContent'];
    flexDirection?: CSSProps['flexDirection'];
    gap?: CSSProps['gap'];
  } & GridContainerProps
>`
  ${(props) => {
    if (!props.container) return css``;
    const { gridSize = 12, rowGap = 0, columnGap = 0 } = props;
    return css`
      display: grid;
      grid-template-columns: repeat(${gridSize}, 1fr);
      row-gap: ${rowGap}px;
      column-gap: ${columnGap}px;
    `;
  }}

  ${(props) =>
    !props.container &&
    css`
      display: flex;
    `}

    ${(props) =>
    !props.container &&
    css`
      display: flex;
    `}
  
    ${(props) =>
    props.alignCenter &&
    css`
      align-items: center;
    `}
  
    ${(props) =>
    props.alignItems &&
    css`
      align-items: ${props.alignItems};
    `}
  
    ${(props) =>
    props.justifyCenter &&
    css`
      justify-content: center;
    `}
  
    ${(props) =>
    props.justifyContent &&
    css`
      justify-content: ${props.justifyContent};
    `}
  
  ${(props) => props.flexDirection && `flex-direction: ${props.flexDirection};`}

  ${(props) => props.gap && `gap: ${props.gap};`}

  ${({ size = 1, nextRow, startColumn }) => {
    if (!nextRow && !startColumn) {
      return css`
        grid-column: span ${size};
      `;
    }

    const columnLine = nextRow ? 1 : startColumn ?? 1;

    return css`
      grid-column: ${columnLine} / span ${size};
    `;
  }}
`;

const Divider = styled.div<{
  color?: CSSProps['color'];
  margin?: CSSProps['margin'];
  size?: number;
}>`
  grid-column: ${({ size = 12 }) => `span ${size}`}};
  border-top: 1px solid #ddd;
  ${({ color }) => color && `border-color: ${color};`}

  ${({ margin }) =>
    margin
      ? `margin: ${margin};`
      : css`
          margin: 8px 0;
        `}
`;

const Spacer = styled.div<{ space?: number; size?: number }>`
  grid-column: ${({ size = 12 }) => `span ${size}`};
  margin-top: ${({ space = 16 }) => `${space}px`};
`;

const Grid = Object.assign(StyledGrid, { Divider, Container, Item, Spacer });

export default Grid;
